import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { COVERAGE_BY_SLUG } from '../../lib/coverage';
import { ProgressBar } from './FormPrimitives';
import { ResumeLinkExpired } from './ResumeLinkExpired';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Token arrives in the URL fragment (#t=...), is kept in sessionStorage and removed from the address bar
const readResumeToken = (leadId) => {
  if (!leadId) return '';
  const key = `hcp_resume_${leadId}`;
  try {
    const m = window.location.hash.match(/[#&]t=([^&]+)/);
    if (m) {
      sessionStorage.setItem(key, m[1]);
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
      return m[1];
    }
    return sessionStorage.getItem(key) || '';
  } catch (_) {
    return '';
  }
};

/**
 * ProfileShell — multi-step container for full Protection Profile.
 * Props:
 *   coverageType: slug
 *   sections: [{ title, subtitle, render(data, update), validate(data) -> string | null }]
 *   intro: string (optional)
 *   personalTouchMessage: string (optional)
 */
const ProfileShell = ({ coverageType, sections, intro, personalTouchMessage }) => {
  const [params] = useSearchParams();
  const leadId = params.get('lead_id');
  const navigate = useNavigate();
  const meta = COVERAGE_BY_SLUG[coverageType];

  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(!!leadId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [completed, setCompleted] = useState(false);
  const [token] = useState(() => readResumeToken(leadId));
  const [denied, setDenied] = useState(false);

  // Load lead context
  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    if (!token) {
      setDenied(true);
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/leads/${leadId}`, { headers: { 'X-Resume-Token': token } })
      .then((res) => {
        // Pre-populate with any partial full_profile data already saved
        const lead = res.data || {};
        const prior = lead.full_profile || {};
        setData({ ...prior });
        setLoading(false);
      })
      .catch((err) => {
        if (err?.response?.status === 401) setDenied(true);
        setLoading(false);
      });
  }, [leadId, token]);

  const update = useCallback((patch) => {
    setData((d) => ({ ...d, ...patch }));
  }, []);

  const save = async ({ markCompleted }) => {
    if (!leadId) {
      setError('Missing lead reference. Please start from the Protection Center.');
      return false;
    }
    setSaving(true);
    setError('');
    try {
      await axios.patch(
        `${API}/leads/${leadId}/profile`,
        { ...data, _completion_status: markCompleted ? 'full_completed' : 'full_started' },
        { headers: { 'Content-Type': 'application/json', 'X-Resume-Token': token }, timeout: 20000 }
      );
      setSaving(false);
      return true;
    } catch (err) {
      setSaving(false);
      if (err?.response?.status === 401) {
        setDenied(true);
        return false;
      }
      const detail = err?.response?.data?.detail || err?.message || 'Save failed';
      setError(`We could not save your progress (${detail}). Your basic info is already saved — try again or call (972) 292-8640.`);
      return false;
    }
  };

  const handleNext = async () => {
    const current = sections[step];
    if (current.validate) {
      const err = current.validate(data);
      if (err) {
        setError(err);
        return;
      }
    }
    setError('');
    const ok = await save({ markCompleted: false });
    if (!ok) return;
    if (step + 1 < sections.length) {
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrev = () => {
    if (step > 0) setStep(step - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    const current = sections[step];
    if (current.validate) {
      const err = current.validate(data);
      if (err) {
        setError(err);
        return;
      }
    }
    setError('');
    const ok = await save({ markCompleted: true });
    if (ok) setCompleted(true);
  };

  // Missing lead reference
  if (!leadId) {
    return (
      <div className="container mx-auto px-4 py-16 text-center max-w-xl">
        <h1 className="text-2xl font-bold mb-3" style={{ color: '#01325B' }}>Let's start with the basics</h1>
        <p className="text-gray-700 mb-6">
          Please start your {meta?.label || 'Protection'} request with a few basic details first — it only takes a minute.
        </p>
        <Link
          to={`/protection-center/start/${coverageType}`}
          className="inline-block px-6 py-3 rounded-lg font-semibold text-white"
          style={{ background: '#E07A2C' }}
        >
          Start Basic Request
        </Link>
      </div>
    );
  }

  if (loading) {
    return <div className="container mx-auto px-4 py-20 text-center text-gray-600">Loading your profile…</div>;
  }

  if (denied && !completed) return <ResumeLinkExpired leadId={leadId} />;

  // Completion view
  if (completed) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-2xl">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 text-center" data-testid="profile-completed">
          <div className="text-5xl mb-3">{meta?.emoji || '🛡️'}</div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: '#01325B' }}>Thank You</h1>
          <p className="text-gray-700 text-lg mb-3">We received your Protection Profile.</p>
          <p className="text-gray-700 mb-8">
            Our team will review your information and follow up with next steps.
          </p>
          <p className="text-xs text-gray-500 italic mb-8">
            Your information is used only to help prepare your insurance quote or coverage review. We do not sell your information.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/protection-center"
              className="px-6 py-3 rounded-lg font-semibold text-white"
              style={{ background: '#E07A2C' }}
            >
              Back to Protection Center
            </Link>
            <a
              href="mailto:Herecomesprotection@gmail.com"
              className="px-6 py-3 rounded-lg font-semibold border-2 inline-flex items-center justify-center"
              style={{ borderColor: '#01325B', color: '#01325B' }}
            >
              Email Herecomesprotection@gmail.com
            </a>
          </div>
        </div>
      </div>
    );
  }

  const current = sections[step];

  return (
    <div data-testid={`profile-${coverageType}`}>
      {/* Header */}
      <section className="py-10" style={{ background: 'linear-gradient(135deg,#01325B 0%, #02466e 100%)' }}>
        <div className="container mx-auto px-4 text-center text-white">
          <div className="text-5xl mb-2">{meta?.emoji}</div>
          <h1 className="text-3xl md:text-4xl font-bold mb-1">{meta?.label} — Full Protection Profile</h1>
          <p className="opacity-90 max-w-2xl mx-auto text-sm md:text-base">
            {intro ||
              'The more details you provide, the more accurate your quote or coverage review may be. Complete what you can, and we will guide you from there.'}
          </p>
        </div>
      </section>

      <section className="py-10 bg-[#f6f5f1]">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-10">
            <ProgressBar step={step} total={sections.length} />

            {personalTouchMessage && step === 0 && (
              <div className="mb-6 p-4 rounded-lg border-l-4" style={{ borderColor: '#E07A2C', background: '#FFF6EE' }}>
                <p className="text-sm italic text-gray-700">{personalTouchMessage}</p>
              </div>
            )}

            <div data-testid={`profile-step-${step}`}>
              {current.render(data, update)}
            </div>

            {error && (
              <div className="mt-5 p-3 rounded-lg bg-red-50 border border-red-300 text-red-800 text-sm" data-testid="profile-error">
                {error}
              </div>
            )}

            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-between items-center">
              <button
                type="button"
                onClick={handlePrev}
                disabled={step === 0 || saving}
                data-testid="profile-prev-btn"
                className="px-5 py-3 rounded-lg font-semibold border-2 disabled:opacity-40 disabled:cursor-not-allowed w-full sm:w-auto"
                style={{ borderColor: '#01325B', color: '#01325B' }}
              >
                ← Back
              </button>

              <div className="text-xs text-gray-500 italic flex items-center gap-2" data-testid="autosave-indicator">
                {saving ? (
                  <>
                    <span className="inline-block w-3 h-3 border-2 border-[#E07A2C] border-t-transparent rounded-full animate-spin"></span>
                    <span>Saving your progress…</span>
                  </>
                ) : (
                  <span>Your progress is saved automatically.</span>
                )}
              </div>

              {step + 1 < sections.length ? (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={saving}
                  data-testid="profile-next-btn"
                  className="px-6 py-3 rounded-lg font-semibold text-white shadow-md disabled:opacity-60 w-full sm:w-auto"
                  style={{ background: '#E07A2C' }}
                >
                  {saving ? 'Saving…' : 'Save & Continue →'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={saving}
                  data-testid="profile-submit-btn"
                  className="px-6 py-3 rounded-lg font-bold text-white shadow-md disabled:opacity-60 w-full sm:w-auto"
                  style={{ background: '#E07A2C' }}
                >
                  {saving ? 'Submitting…' : 'Submit My Protection Profile'}
                </button>
              )}
            </div>
          </div>

          <p className="text-xs text-gray-500 italic text-center mt-4">
            You can stop and return anytime — your progress is saved against your lead.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ProfileShell;
